import React, {
  useState,
} from "react";
import {
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import {
  useDispatch,
  useSelector,
} from "react-redux";
import {
  createRazorpayOrder,
  verifyRazorpayPayment,
} from "../../actions/paymentActions";
import {
  useCart,
} from "../../components/CartContext";
import { useRestaurantSession } from "../../components/RestaurantSessionProvider";
import "./css/PaymentScreen.css";
/*
\=========================================================
LOAD RAZORPAY
\=========================================================
*/
function loadRazorpay() {
  return new Promise((resolve) => {
    if (
      window.Razorpay
    ) {
      resolve(true);
      return;
    }
    const script =
      document.createElement(
        "script"
      );
    script.src =
      "https\://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => {
      resolve(true);
    };
    script.onerror = () => {
      resolve(false);
    };
    document.body.appendChild(
      script
    );
  });
}
/*
\=========================================================
PAYMENT SCREEN
\=========================================================
*/
function PaymentScreen() {
  const {
    restaurantId,
    tableId,
  } = useParams();
  const navigate =
    useNavigate();
  const location =
    useLocation();
  const dispatch =
    useDispatch();
  const { sessionToken } = useRestaurantSession();
  const createdOrder = useSelector((state) => state.order?.order);
  /*
  \=======================================================
  CART
  \=======================================================
  */
  const {
    cart,
    clearCart,
  } = useCart();
  /*
  \=======================================================
  RESTAURANT
  \=======================================================
  */
  const themeState =
    useSelector(
      (state) =>
        state.theme || {}
    );
  const restaurantName =
    themeState.restaurantName ||
    themeState.restaurant_name ||
    "CYMA GREEK TAVERNA";
  /*
  \=======================================================
  PAYMENT STATE
  \=======================================================
  */
  const paymentState =
    location.state || {};
  const orderId =
    createdOrder?.id || paymentState.orderId;
  const subtotal =
    Number(
      paymentState.subtotal || 0
    );
  const tax =
    Number(
      paymentState.tax || 0
    );
  const serviceCharge =
    Number(
      paymentState.serviceCharge || 0
    );
  const total = Number(createdOrder?.total ?? paymentState.total ?? 0);
  /*
  \=======================================================
  LOCAL STATE
  \=======================================================
  */
  const [
    loading,
    setLoading,
  ] = useState(false);
  const [
    error,
    setError,
  ] = useState("");
  /*
  \=======================================================
  BACK TO ORDER
  \=======================================================
  */
  const handleBack = () => {
    navigate(
      `/${restaurantId}/table/${tableId}/order`,
      {
        state: {
          subtotal,
          tax,
          serviceCharge,
          total,
        },
      }
    );
  };
  /*
  \=======================================================
  PAYMENT
  \=======================================================
  */
  const handlePayment =
    async () => {
      /*
      \---------------------------------------------------
      CHECK ORDER
      \---------------------------------------------------
      */
      if (loading) return;
      if (!sessionToken) {
        setError("Dining session is not ready. Please scan the table QR code again.");
        return;
      }
      if (!orderId) {
        setError(
          "Order information is missing."
        );
        return;
      }
      try {
        setLoading(true);
        setError("");
        /*
        \---------------------------------------------------
        LOAD RAZORPAY
        \---------------------------------------------------
        */
        const razorpayLoaded =
          await loadRazorpay();
        if (!razorpayLoaded) {
          throw new Error(
            "Unable to load secure payment gateway."
          );
        }
        /*
        \---------------------------------------------------
        CREATE RAZORPAY ORDER
        \---------------------------------------------------
        */
        const response =
          await dispatch(
            createRazorpayOrder(
              restaurantId,
              orderId,
              sessionToken
            )
          );
        /*
        \---------------------------------------------------
        BACKEND RESPONSE
        \---------------------------------------------------
        */
        const paymentData =
          response?.data ||
          response;
        const keyId =
          paymentData?.key_id;
        const razorpayOrderId =
          paymentData?.razorpay_order_id;
        const amount = Number(paymentData?.amount);
        const currency =
          paymentData?.currency ||
          "INR";
        const backendRestaurantName =
          paymentData?.restaurant_name ||
          restaurantName;
        if (
          !keyId || !razorpayOrderId ||
          !Number.isFinite(amount) || amount <= 0
        ) {
          throw new Error(
            "Invalid payment information received from server."
          );
        }
        /*
        \---------------------------------------------------
        RAZORPAY OPTIONS
        \---------------------------------------------------
        */
        const options = {
          key:
            keyId,
          amount: amount,
          currency:
            currency,
          name:
            backendRestaurantName,
          description:
            `Order #${orderId}`,
          order_id:
            razorpayOrderId,
          handler:
            async function (
              paymentResponse
            ) {
              try {
                setLoading(
                  true
                );
                setError("");
                /*
                \-------------------------------------------
                VERIFY PAYMENT
                \-------------------------------------------
                */
                await dispatch(
                  verifyRazorpayPayment(
                    restaurantId,
                    {
                      order_id:
                        orderId,
                      razorpay_order_id:
                        paymentResponse.razorpay_order_id,
                      razorpay_payment_id:
                        paymentResponse.razorpay_payment_id,
                      razorpay_signature:
                        paymentResponse.razorpay_signature,
                    },
                    sessionToken
                  )
                );
                /*
                \-------------------------------------------
                PAYMENT SUCCESS
                \-------------------------------------------
                */
                clearCart();
                navigate(
                  `/${restaurantId}/table/${tableId}/success`,
                  {
                    state: {
                      orderId:
                        orderId,
                      paymentMethod:
                        "ONLINE",
                      total:
                        total,
                    },
                  }
                );
              } catch (verificationError) {
                console.error(
                  "Payment verification error:",
                  verificationError
                );
                setError(
                  verificationError
                    ?.response
                    ?.data
                    ?.detail ||
                  verificationError
                    ?.response
                    ?.data
                    ?.message ||
                  verificationError?.message ||
                  "Payment verification failed."
                );
              } finally {
                setLoading(
                  false
                );
              }
            },
          prefill: {
            name:
              "",
            email:
              "",
            contact:
              "",
          },
          notes: {
            restaurant_id:
              restaurantId,
            table_id:
              tableId,
            order_id:
              orderId,
          },
          theme: {
            color:
              "#1557a6",
          },
          modal: {
            ondismiss:
              function () {
                setLoading(
                  false
                );
              },
          },
        };
        /*
        \---------------------------------------------------
        OPEN RAZORPAY
        \---------------------------------------------------
        */
        const razorpay =
          new window.Razorpay(
            options
          );
        razorpay.on(
          "payment.failed",
          function (
            paymentError
          ) {
            console.error(
              "Razorpay payment failed:",
              paymentError
            );
            setError(
              paymentError
                ?.error
                ?.description ||
              "Payment failed. Please try again."
            );
            setLoading(
              false
            );
          }
        );
        razorpay.open();
      } catch (err) {
        console.error(
          "Payment error:",
          err
        );
        setError(
          err?.response?.data?.detail ||
          err?.response?.data?.message ||
          err?.message ||
          "Unable to start payment."
        );
        setLoading(
          false
        );
      }
    };
  /*
  \=======================================================
  MISSING ORDER
  \=======================================================
  */
  if (!orderId) {
    return (
      <main className="theme5-payment-page">
        <div className="theme5-payment-empty">
          <span>
            PAYMENT
          </span>
          <h1>
            ORDER
            <br />
            NOT FOUND.
          </h1>
          <p>
            We could not find the order
            information required for payment.
          </p>
          <button
            type="button"
            onClick={() =>
              navigate(
                `/${restaurantId}/table/${tableId}`
              )
            }
          >
            RETURN TO MENU
            <span>→</span>
          </button>
        </div>
      </main>
    );
  }
  /*
  \=======================================================
  PAGE
  \=======================================================
  */
  return (
    <main className="theme5-payment-page">
      {/* =================================================
          HERO
      \================================================= */}
      <section className="theme5-payment-hero">
        <div className="theme5-payment-container">
          <button
            type="button"
            className="theme5-payment-back"
            onClick={handleBack}
          >
            <span>
              ←
            </span>
            BACK TO ORDER
          </button>
          <div className="theme5-payment-heading">
            <span>
              {restaurantName}
            </span>
            <h1>
              PAYMENT
            </h1>
            <p>
              SECURE YOUR ORDER
            </p>
          </div>
        </div>
      </section>
      {/* =================================================
          MAIN
      \================================================= */}
      <section className="theme5-payment-content">
        <div className="theme5-payment-container">
          <div className="theme5-payment-layout">
            {/* =============================================
                MAIN PAYMENT CARD
            \============================================= */}
            <div className="theme5-payment-main">
              <div className="theme5-payment-card">
                <div className="theme5-payment-card-top">
                  <span>
                    SECURE PAYMENT
                  </span>
                  <div className="theme5-payment-lock">
                    🔒
                  </div>
                </div>
                <div className="theme5-payment-card-heading">
                  <h2>
                    COMPLETE
                    <br />
                    YOUR PAYMENT.
                  </h2>
                  <p>
                    Your order has been created.
                    Complete the secure online
                    payment to confirm your table order.
                  </p>
                </div>
                {/* ORDER */}
                <div className="theme5-payment-order-info">
                  <div>
                    <span>
                      ORDER
                    </span>
                    <strong>
                      #{orderId}
                    </strong>
                  </div>
                  <div>
                    <span>
                      TABLE
                    </span>
                    <strong>
                      {tableId}
                    </strong>
                  </div>
                </div>
                {/* ERROR */}
                {(error || !sessionToken) && (
                  <div className="theme5-payment-error">
                    {error || "Dining session unavailable. Please scan the table QR code again."}
                  </div>
                )}
                {/* PAY BUTTON */}
                <button
                  type="button"
                  className="theme5-payment-button"
                  onClick={
                    handlePayment
                  }
                  disabled={loading || !sessionToken}
                >
                  {loading ? (
                    <>
                      <span className="theme5-payment-spinner" />
                      PROCESSING...
                    </>
                  ) : (
                    <>
                      PAY NOW
                      <span>
                        →
                      </span>
                    </>
                  )}
                </button>
                <p className="theme5-payment-secure-text">
                  You will be redirected to
                  Razorpay's secure payment window.
                </p>
              </div>
            </div>
            {/* =============================================
                SUMMARY
            \============================================= */}
            <aside className="theme5-payment-summary">
              <span className="theme5-payment-summary-label">
                ORDER SUMMARY
              </span>
              <h2>
                YOUR
                <br />
                ORDER
              </h2>
              <div className="theme5-payment-summary-line" />
              <div className="theme5-payment-summary-row">
                <span>
                  ITEMS
                </span>
                <strong>
                  {cart.length}
                </strong>
              </div>
              <div className="theme5-payment-summary-row">
                <span>
                  SUBTOTAL
                </span>
                <strong>
                  ₹
                  {subtotal.toFixed(2)}
                </strong>
              </div>
              <div className="theme5-payment-summary-row">
                <span>
                  TAX
                </span>
                <strong>
                  ₹
                  {tax.toFixed(2)}
                </strong>
              </div>
              <div className="theme5-payment-summary-row">
                <span>
                  SERVICE CHARGE
                </span>
                <strong>
                  ₹
                  {serviceCharge.toFixed(2)}
                </strong>
              </div>
              <div className="theme5-payment-total">
                <span>
                  TOTAL
                </span>
                <strong>
                  ₹
                  {total.toFixed(2)}
                </strong>
              </div>
              <div className="theme5-payment-security">
                <span>
                  🔒
                </span>
                <div>
                  <strong>
                    SECURE PAYMENT
                  </strong>
                  <p>
                    Your payment is processed
                    securely through Razorpay.
                  </p>
                </div>
              </div>
            </aside>
          </div>
          {/* =================================================
              BOTTOM
          \================================================= */}
          <div className="theme5-payment-bottom">
            <span>
              OPA!
            </span>
            <p>
              Good food. Good friends.
              Good times.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}
export default PaymentScreen;